import { useNavigate } from 'react-router';

import styled from '@emotion/styled';

import Formtainer from '../../../components/Authlayout/Formtainer';
import ValidatingFormSubmitButton from '../../../components/Button/FormSubmitButton';
import SwitchAccountButton from '../../../components/Button/SwitchAccountButton';
import { FormComponent } from '../../../components/Form/FormComponent';
import {
  Checkbox,
  PasswordInputField,
  TextInputField,
} from '../../../components/Form/FormField';
import { InternalNavLink } from '../../../components/Link/Link';
import { configSetting } from '../../../config';
import useUserSignin from '../../../hooks/auth/useUserSignin';
import {
  LocumSignInDataType,
  locumSigninInitialValues,
  LocumSignInSchema,
} from '../../../models/auth/signIn.model';
import {
  ACCOUNT,
  FORGOT_PASSWORD,
  SIGNUP,
  VIEW_ONLY_JOBS,
} from '../../../routes/routeConstants';
import useGetGuestJobs from '../../../hooks/guest/useGetGuestJobs';
import { useEffect, useRef, useState } from 'react';
import DropdownButton from '../../../components/Button/DropdownButton';
import Page from '../../../components/Page/Page';

const SignIn = () => {
  const { signinUser, loading: userSiginLoading } = useUserSignin();
  const navigate = useNavigate();
  const onSubmit = async (values: LocumSignInDataType) => {
    signinUser({ ...values, email: values.email.toLowerCase() });
  };

  const { getGuestJobs } = useGetGuestJobs();

  useEffect(() => {
    getGuestJobs();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2) State + ref to track current & previous selection
  const [accountType, setAccountType] = useState<string>('Locum');
  const prevAccountTypeRef = useRef<string>(accountType);

  // 3) Handle dropdown selection
  const handleAccountSelect = (opt: string) => {
    prevAccountTypeRef.current = opt;
    setAccountType(opt);
  };

  return (
    <Page title="Locumspace | SignIn">
      <Formtainer header="SIGN INTO YOUR ACCOUNT" className="signin">
        <Buttons>
          {' '}
          <DropdownButton
            options={['Locum', 'HR/Recruiter']}
            value={accountType}
            onSelect={handleAccountSelect}
          />
          <SwitchAccountButton
            path={`${configSetting.healthDashboardUrl}/account/signin/health-signin`}
          />
        </Buttons>
        <FormComponent
          initialValues={locumSigninInitialValues}
          schema={LocumSignInSchema}
          onSubmit={onSubmit}
        >
          <TextInputField
            label="Email Address"
            name="email"
            id="email"
            type="email"
            placeholder="Email"
          />
          <PasswordInputField
            label="Password"
            name="password"
            type="password"
            id="password"
            placeholder="Password"
          />
          <RememberMeAndForgotPassword>
            <Checkbox name="rememberMe" className="terms">
              Remember me
            </Checkbox>
            <p
              style={{ cursor: 'pointer' }}
              onClick={() => {
                navigate(`/${ACCOUNT}/${FORGOT_PASSWORD}`);
              }}
            >
              Forgot Password?
            </p>
          </RememberMeAndForgotPassword>
          <ValidatingFormSubmitButton
            className="fillParent"
            loading={userSiginLoading}
          >
            Login
          </ValidatingFormSubmitButton>
        </FormComponent>
      </Formtainer>
      <P>
        Don't have an account?{' '}
        <InternalNavLink to={`/${ACCOUNT}/${SIGNUP}`} className="regular">
          Sign Up
        </InternalNavLink>
      </P>
      or
      <P>
        Continue as
        <InternalNavLink to={`/${VIEW_ONLY_JOBS}`} className="regular">
          Guest
        </InternalNavLink>
      </P>
      <ForgotPasswordMobile
        style={{ cursor: 'pointer' }}
        onClick={() => {
          navigate(`/${ACCOUNT}/${FORGOT_PASSWORD}`);
        }}
      >
        Forgot Password?
      </ForgotPasswordMobile>
    </Page>
  );
};

export default SignIn;

const RememberMeAndForgotPassword = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${({ theme }) => theme.palette.blackBlack2};
  font-weight: 500;
  font-size: 18px;

  & > p {
    color: ${({ theme }) => theme.palette.mainBlue};

    @media (max-width: 768px) {
      display: none;
    }
  }
`;

const P = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
  font-size: 18px;
  color: ${({ theme }) => theme.palette.blackBlack2};
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const ForgotPasswordMobile = styled(P)`
  color: ${({ theme }) => theme.palette.mainBlue};
  border-top: 1px solid ${({ theme }) => theme.palette.greyGrey2};
  margin-top: 1rem;
  padding-top: 1rem;

  @media (min-width: 768px) {
    display: none;
    color: ${({ theme }) => theme.palette.mainBlue};
  }
`;

const Buttons = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 480px) {
    justify-content: center;
    align-items: center;
  }
`;
